import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Send, Users, Mail } from "lucide-react";
import { API } from "@/lib/api";

const KUNDEN_STATUS = [
  { id: "", label: "Alle Kunden" },
  { id: "lead", label: "Nur Leads" },
  { id: "aktiv", label: "Nur Aktive" },
  { id: "inaktiv", label: "Nur Inaktive" },
  { id: "vip", label: "Nur VIP" },
];

export const CampaignsCard = () => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [tag, setTag] = useState("");
  const [kundenStatus, setKundenStatus] = useState("");
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try { const r = await axios.get(`${API}/crm/campaigns`); setHistory(r.data || []); }
    catch { setHistory([]); }
  };
  useEffect(() => { loadHistory(); }, []);

  const checkRecipients = async () => {
    try {
      const r = await axios.post(`${API}/crm/campaigns/preview`, { tag, kunden_status: kundenStatus, subject, message });
      setPreview(r.data);
      if (r.data.count === 0) toast.info("Keine Empfänger mit E-Mail für diese Auswahl");
    } catch (e) { toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen"); }
  };

  const send = async () => {
    if (!subject.trim()) { toast.error("Bitte Betreff angeben"); return; }
    if (!message.trim()) { toast.error("Bitte Nachricht schreiben"); return; }
    const r = await axios.post(`${API}/crm/campaigns/preview`, { tag, kunden_status: kundenStatus }).catch(() => null);
    const count = r?.data?.count ?? "?";
    if (!window.confirm(`Newsletter jetzt an ${count} Empfänger senden?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/crm/campaigns/send`, { subject, message, tag, kunden_status: kundenStatus, origin_url: window.location.origin });
      toast.success(`Versendet: ${res.data.sent} E-Mail(s)${res.data.failed ? ` · ${res.data.failed} fehlgeschlagen` : ""}`);
      setSubject(""); setMessage(""); setPreview(null);
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Versand fehlgeschlagen"); }
    finally { setSending(false); }
  };

  return (
    <Card className="p-6 bg-white border-[var(--brand-border)]" data-testid="campaigns-card">
      <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)] mb-1 flex items-center gap-2">
        <Mail className="w-5 h-5 text-[var(--brand-primary)]" /> Newsletter &amp; Kampagnen
      </h2>
      <p className="text-sm text-[var(--brand-muted)] mb-4">
        Sende eine E-Mail an alle Kunden oder ein Segment (Tag / Status) – im Design deiner Marke. Voraussetzung: E-Mail-Versand ist in den Einstellungen aktiv. Jede E-Mail enthält automatisch einen <b>DSGVO-Abmeldelink</b>; abgemeldete Kunden werden nicht mehr angeschrieben.
      </p>

      <div className="space-y-3">
        <div>
          <Label>Betreff</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="z. B. Neuigkeiten & Angebote im Juli" data-testid="campaign-subject" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Segment: Kunden-Tag (optional)</Label>
            <Input value={tag} onChange={(e) => { setTag(e.target.value); setPreview(null); }} placeholder="z. B. stammkunde" data-testid="campaign-tag" />
          </div>
          <div>
            <Label>Segment: Kunden-Status</Label>
            <select value={kundenStatus} onChange={(e) => { setKundenStatus(e.target.value); setPreview(null); }}
              className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" data-testid="campaign-status">
              {KUNDEN_STATUS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label>Nachricht</Label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6}
            placeholder={"Liebe Kundinnen und Kunden,\n\nwir haben Neuigkeiten für Sie …"} data-testid="campaign-message" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={checkRecipients} className="border-[var(--brand-border)]" data-testid="campaign-preview-btn">
            <Users className="w-4 h-4 mr-1" /> Empfänger prüfen
          </Button>
          {preview && (
            <span className="text-sm text-[var(--brand-muted)]" data-testid="campaign-preview-count">
              <b className="text-[var(--brand-ink)]">{preview.count}</b> Empfänger{preview.sample?.length ? ` (z. B. ${preview.sample.slice(0, 2).join(", ")})` : ""}
            </span>
          )}
          <div className="flex-1" />
          <Button onClick={send} disabled={sending} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="campaign-send-btn">
            <Send className="w-4 h-4 mr-1" /> {sending ? "Sendet …" : "Jetzt senden"}
          </Button>
        </div>
      </div>

      {history.length > 0 && (
        <div className="mt-5 pt-4 border-t border-[var(--brand-border-light)]">
          <div className="text-[11px] uppercase tracking-wider text-[var(--brand-muted)] font-semibold mb-2">Bisherige Kampagnen</div>
          <div className="space-y-1.5" data-testid="campaign-history">
            {history.slice(0, 5).map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 text-sm rounded-lg border border-[var(--brand-border-light)] px-3 py-2" data-testid={`campaign-history-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-[var(--brand-ink)] truncate">{c.subject}</div>
                  <div className="text-[11px] text-[var(--brand-muted)]">
                    {new Date(c.created_at).toLocaleDateString("de-DE")} · {c.sent} gesendet{c.failed ? ` · ${c.failed} Fehler` : ""}{c.tag ? ` · Tag: ${c.tag}` : ""}{c.kunden_status ? ` · Status: ${c.kunden_status}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};
